import React, { useState } from "react";
import CustomerAccountRegistrationForm from "features/registration/components/customerAccountRegistration/CustomerAccountRegistrationForm";
import CustomerAccountRegistrationDTO from "features/registration/dtos/CustomerAccountRegistrationDTO";
import { useInjection } from "infrastructure/ioc/ioc.react";
import IRepository from "data/IRepository";
import { IocTypes } from "infrastructure/ioc/ioc.types";
import { trackPromise } from "react-promise-tracker";
import { Registration_RegisterCustomerAccount, Registration_SendCustomerNotFoundInfo } from "data/endpoints";
import { loadDynamicFormConfig } from "features/shared/helpers/modules";
import ActionResultDTO from "features/shared/dtos/ActionResultDTO";
import { CustomerAccountRegistrationResult } from "features/registration/enums/customerAccountRegistrationResult";
import PostResponse from "data/PostResponse";
import IContentDialogService from "features/shared/components/contentDialog/IContentDialogService";
import DataDescriptorDTO from "features/shared/dtos/DataDescriptorDTO";
import { useTranslation } from "react-i18next";
import RegistrationResultSubDialog from "../components/customerAccountRegistration/RegistrationResultSubDialog";
import { useHistory } from "react-router-dom";
import UserManager from "infrastructure/oidc/UserManager";
import DynamicComponetWrapper from "features/shared/components/dynamicComponetWrapper/DynamicComponetWrapper";
import CustomerAccountRegistrationFormBaseProps from "features/registration/components/customerAccountRegistration/CustomerAccountRegistrationFormBaseProps";
import FormFieldDescriptor from "features/shared/forms/dynamicForm/models/FormFieldDescriptor";

const registrationBasicFields = loadDynamicFormConfig("customerAccountRegistrationBasicForm");
const registrationAdditionalFields = loadDynamicFormConfig("customerAccountRegistrationAdditionalForm");

const CustomerAccountRegistrationSection = () => {
  // DI
  const registrationRepo = useInjection<IRepository<CustomerAccountRegistrationDTO>>(IocTypes.Repository);
  const dialogService = useInjection<IContentDialogService>(IocTypes.ContentDialogService);

  // translation
  const tranCommodityDetail = useTranslation("contracts\\enums\\commodityDetail").t;
  const tranCusAccountRegResult = useTranslation("registration\\enums\\customerAccountRegistrationResult").t;
  const tranRegSection = useTranslation("registration\\registration").t ;

  // router
  const history = useHistory();

  // state
  const [showAdditionForm, setShowAdditionForm] = useState(false);
  const [customFormFiedls, setCustomFormFiedls] = useState<FormFieldDescriptor[]>([]);

  // helpers
  const sendRequest = async (
    registration: CustomerAccountRegistrationDTO,
    url: string
  ): Promise< PostResponse<ActionResultDTO<CustomerAccountRegistrationResult>>> => {
    return await trackPromise(
      registrationRepo.post<ActionResultDTO<CustomerAccountRegistrationResult>>(
        {
          url: url,
          data: registration,
        }
      )
    );
  };

  const sendCustomerNotFoundInfo = async (
    registration: CustomerAccountRegistrationDTO
  ): Promise<PostResponse<ActionResultDTO<CustomerAccountRegistrationResult>>> => {
    let newRegistration: CustomerAccountRegistrationDTO = {
      registrationDescriptors: fillDescriptorLabels(registration.registrationDescriptors!)
    }

    return await sendRequest(newRegistration, Registration_SendCustomerNotFoundInfo);
  };

  const fillDescriptorLabels = (descriptors: DataDescriptorDTO[]): DataDescriptorDTO[] => {
    let result: DataDescriptorDTO[] = [];
    let allFormDesriptors = [...registrationBasicFields, ...registrationAdditionalFields, ...customFormFiedls];

    for (const dataDesc of descriptors) {
      let formDesc = allFormDesriptors.find(x => x.name === dataDesc.code);
      
      if (formDesc) {
        let newDataDesc = {...dataDesc};
        newDataDesc.codeLabel = formDesc.label;

        if (newDataDesc.code === "CommodityDetail") {
          newDataDesc.valueLabel = tranCommodityDetail(newDataDesc.value!);
        } else {
          newDataDesc.valueLabel = newDataDesc.value;
        }

        result.push(newDataDesc);
      }
    }

    return result;
  };

  // handlers
  const customerAccountRegistrationHandler = async (registration: CustomerAccountRegistrationDTO) => {
    let response: PostResponse<ActionResultDTO<CustomerAccountRegistrationResult>>;
    // register customer account
    if (!showAdditionForm) {
      response = await sendRequest(registration, Registration_RegisterCustomerAccount);
    } 
    // send additional info (customer not found)
    else {
      response = await sendCustomerNotFoundInfo(registration);
    }
    // open info dialog
    dialogService.openDialog(tranRegSection("customerAccountRegistrationSection.accountRegistration"), () => (
      <RegistrationResultSubDialog
        result={response}
        regResultTranslate={tranCusAccountRegResult}
        onLoginPageClick={backToLoginClickHandler}
        onAddtionalFormClick={showAdditionalFormHandler}
      />
    ), 
    false
    );
  }; 

  const backToLoginClickHandler = () => { 
    UserManager.signinRedirect();
  }

  const showAdditionalFormHandler = () => { 
    dialogService.closeDialog();
    setShowAdditionForm(true);
  }

  // sub-renders
  const renderRegistrationForm = () => { 
    let baseProps: CustomerAccountRegistrationFormBaseProps = {
      additionalFormFields: registrationAdditionalFields,
      showAdditionalForm: showAdditionForm,
      onSubmit: customerAccountRegistrationHandler,
      onBackClick: backToLoginClickHandler,
      setCustomFormFiedls: setCustomFormFiedls
    }

    return (
      <DynamicComponetWrapper
        baseComponentName="CustomerAccountRegistrationForm"
        componentProps={baseProps}
        defaultComponentRender={() => (
          <CustomerAccountRegistrationForm
            basicFormFields={registrationBasicFields}
            {...baseProps}
          />
        )}
      />
    );
  }

  // render 
  return (
    <div className="row no-gutters justify-content-center mt-md-5">
      {renderRegistrationForm()}
    </div>
  );
};

export default CustomerAccountRegistrationSection;
